<template>
  <div class="page-shell td9-page">
    <!-- ============ 工具栏 ============ -->
    <div class="toolbar">
      <span class="toolbar-label">信号日</span>
      <el-select v-model="date" class="sel-date" size="small" @change="loadSignals">
        <el-option v-for="d in dates" :key="d" :value="d" :label="dLabel(d)" />
      </el-select>

      <el-radio-group v-model="dir" size="mini" @change="loadSignals">
        <el-radio-button label="all">全部</el-radio-button>
        <el-radio-button label="down">低九 · 买</el-radio-button>
        <el-radio-button label="up">高九 · 卖</el-radio-button>
      </el-radio-group>

      <span class="toolbar-label">股票池</span>
      <el-select v-model="pool" class="sel-pool" size="small" @change="loadSignals">
        <el-option value="all" :label="`全市场（${pools.all}）`" />
        <el-option value="top500" :label="`Top500（${pools.top500}）`" />
        <el-option value="p75" :label="`P75 机构池（${pools.p75}）`" />
      </el-select>

      <el-checkbox v-model="exact" size="mini" @change="loadSignals">只看新出（=9）</el-checkbox>

      <span class="toolbar-sep" />

      <span class="toolbar-label">优化过滤</span>
      <el-tooltip :content="f1Tip" placement="bottom">
        <el-checkbox v-model="f1" size="mini">F1 非下跌趋势</el-checkbox>
      </el-tooltip>
      <el-tooltip :content="f2Tip" placement="bottom">
        <el-checkbox v-model="f2" size="mini">F2 放量 ≥{{ params.volMult }}×</el-checkbox>
      </el-tooltip>
      <el-tooltip :content="f3Tip" placement="bottom">
        <el-checkbox v-model="f3" size="mini">F3 站上 MA5</el-checkbox>
      </el-tooltip>
      <span v-if="covText" class="cov-hint">{{ covText }}</span>

      <span class="toolbar-fill" />
      <el-popover
        placement="bottom-end"
        width="440"
        trigger="hover"
        :open-delay="80"
        popper-class="td9-notice-popper"
      >
        <div class="td9-notice-pop">
          <div class="td9-notice-title">本页是「候选池生成器」，不是能赚钱的策略</div>
          <p>
            <b>① 为什么不接进【策略回测】</b>：九转是<b>时序事件信号</b>（计数只在满足条件的个股上成立、其余全为 0），
            与现有「横截面排序取 Top-N」的因子框架<b>不是一套统计口径</b>，强行并入会让排序退化成「在并列的 0 里凑数」。
          </p>
          <p>
            <b>② 实测结论</b>（research-notes/td9-round1-sequential-setup-2026-09-27.html）：
            低九信号自身毛超额 ≈ 0、超额胜率仅 44~46%；组合层四种持有期<b>全部跑输全市场等权买入持有</b>。
            把本页与其它条件叠加使用，不要单独照做。
          </p>
          <p>
            <b>③ 口径</b>：用 <b>不复权价</b>（daily_bar）计算，与<b>东财盘面一致</b>；
            除权跳空可能制造假信号，窗口内命中的已打「除权」标签。
          </p>
        </div>
        <button slot="reference" type="button" class="notice-trigger" aria-label="页面说明">
          <i class="el-icon-warning-outline" />
          <span>说明</span>
        </button>
      </el-popover>
      <span class="caliber-chip">{{ meta ? meta.priceCaliber : '' }}</span>
      <el-button size="small" icon="el-icon-download" :disabled="!shownRows.length" @click="exportCsv">导出</el-button>
      <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="loadAll">刷新</el-button>
    </div>

    <div v-if="error" class="error-bar">
      <el-alert :title="error" type="warning" :closable="false" show-icon />
    </div>

    <template v-if="meta">
      <!-- ============ 概览卡 ============ -->
      <div class="stat-cards">
        <div class="stat-card">
          <div class="stat-label">当前列表 · 低九（潜在买点）</div>
          <div class="stat-value up">{{ lowCount }}</div>
          <div class="stat-sub">{{ scopeText }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">当前列表 · 高九（潜在卖点）</div>
          <div class="stat-value down">{{ highCount }}</div>
          <div class="stat-sub">{{ scopeText }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">近 5 个交易日信号数（全市场·计数≥9）</div>
          <div class="spark">
            <div
              v-for="(r, i) in meta.recent"
              :key="r.date"
              :title="`${r.date}：低九 ${r.down} / 高九 ${r.up}`"
              :style="{ height: sparkHeight(r), background: i === 0 ? '#409EFF' : '#c6e2ff' }"
            />
          </div>
          <div class="stat-sub">{{ meta.recent.map(r => shortDate(r.date)).join(' · ') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">市场环境（F1 判定依据）</div>
          <div class="stat-value stat-value-sm">
            <el-tag :type="market.regime === 'range' ? 'warning' : 'danger'" size="mini">
              {{ market.regime === 'range' ? '震荡市' : market.regime === 'trend' ? '趋势市' : '未知' }}
            </el-tag>
            <span class="ml6">{{ market.aboveMa20 ? '站上 MA20' : '未站上 MA20' }}</span>
            <span class="ml6 txt-muted">F1 {{ market.notDown ? '✓ 通过' : '✗ 不通过' }}</span>
          </div>
          <div class="stat-sub">
            合成等权指数 {{ market.close }}（当日 {{ signed(market.dayChg) }}%）· MA20 {{ market.ma20 }} · ADX(14) {{ market.adx }}
          </div>
        </div>
      </div>

      <!-- ============ 信号列表 ============ -->
      <div class="block">
        <div class="block-head">
          <h3>当期九转信号列表</h3>
          <span class="hint">
            共 <b>{{ shownRows.length }}</b> 条<template v-if="filterActive">（过滤前 {{ rows.length }} 条）</template>
            · 点击任意行查看该股九转计数图
          </span>
          <span class="hint ml-auto">排序：方向 → 计数 → 量比</span>
        </div>
        <el-table
          ref="table"
          :data="shownRows"
          size="mini"
          border
          stripe
          height="360"
          :row-class-name="rowClass"
          @row-click="onRowClick"
        >
          <el-table-column label="#" type="index" width="46" align="center" />
          <el-table-column label="代码" width="88">
            <template slot-scope="{ row }">
              <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">{{ row.code }}</el-link>
            </template>
          </el-table-column>
          <el-table-column label="名称" min-width="104">
            <template slot-scope="{ row }">
              <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">{{ row.name || '—' }}</el-link>
            </template>
          </el-table-column>
          <el-table-column label="方向" width="82" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="row.dir === 'down' ? 'danger' : 'success'" size="mini">
                {{ row.dir === 'down' ? '低九 · 买' : '高九 · 卖' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="计数" width="62" align="center" sortable :sort-method="(a, b) => a.count - b.count">
            <template slot-scope="{ row }">
              <el-tag v-if="row.count === 9" size="mini" :type="row.dir === 'down' ? 'danger' : 'success'">{{ row.count }}</el-tag>
              <span v-else class="count-run">{{ row.count }}</span>
            </template>
          </el-table-column>
          <el-table-column label="收盘" width="76" align="right">
            <template slot-scope="{ row }">{{ row.close }}</template>
          </el-table-column>
          <el-table-column label="涨跌" width="80" align="right">
            <template slot-scope="{ row }">
              <span :class="clsOf(row.changePct)">{{ signed(row.changePct) }}%</span>
            </template>
          </el-table-column>
          <el-table-column label="量比" width="70" align="right">
            <template slot-scope="{ row }">
              <span :class="row.f2Pass ? 'txt-strong' : ''">{{ row.volRatio === null ? '—' : row.volRatio }}</span>
            </template>
          </el-table-column>
          <el-table-column label="距 MA5" width="80" align="right">
            <template slot-scope="{ row }">
              <span :class="clsOf(row.ma5Dist)">{{ row.ma5Dist === null ? '—' : signed(row.ma5Dist) + '%' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="大盘环境" width="96" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.f1Pass ? 'warning' : 'info'">
                {{ row.f1Pass ? (market.regime === 'range' ? '震荡市' : '趋势市') : '下跌趋势' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="流通市值" width="92" align="right">
            <template slot-scope="{ row }">{{ row.cap === null ? '—' : row.cap.toLocaleString() }}</template>
          </el-table-column>
          <el-table-column label="九转序列" width="118">
            <template slot-scope="{ row }">
              <span class="seq">
                <i
                  v-for="k in maxSeq"
                  :key="k"
                  :class="k <= Math.min(row.count, maxSeq) ? (row.dir === 'down' ? 'on-b' : 'on-s') : ''"
                />
              </span>
            </template>
          </el-table-column>
          <el-table-column label="标记" width="76" align="center">
            <template slot-scope="{ row }">
              <el-tag v-if="row.exDiv" size="mini" type="warning">除权</el-tag>
              <span v-else class="txt-muted">—</span>
            </template>
          </el-table-column>
        </el-table>
        <div class="legend">
          <span><i class="dot dot-red" />低九（潜在买点）</span>
          <span><i class="dot dot-green" />高九（潜在卖点）</span>
          <span>计数 &gt; 9 = 计数未重置（「钝化」，研究报告追踪过）</span>
          <span><b>量比</b>加粗 = 满足 F2 放量条件</span>
        </div>
      </div>

      <!-- ============ 九转 K 线 ============ -->
      <div class="block">
        <div class="block-head">
          <h3>{{ chartTitle }}</h3>
          <span class="hint">数字 1~9 = 该波累计到第几根；红=低九方向（数字在下方）、绿=高九方向（数字在上方）。计数越过 9 后不再标注</span>
        </div>
        <div class="chart-panel" v-loading="chartLoading" element-loading-text="正在读取K线...">
          <el-alert
            v-if="chartError"
            :title="chartError"
            type="info"
            :closable="false"
            show-icon
            class="chart-error"
          />
          <div v-else-if="chart" class="legend">
            <span><i class="dot dot-red" />低九计数</span>
            <span><i class="dot dot-green" />高九计数</span>
            <span><i class="line line-ma5" />MA5</span>
            <span><i class="line line-ma20" />MA20</span>
            <span class="txt-muted">K 线：红 = 涨、绿 = 跌（A 股惯例）</span>
            <span v-if="chart.exDates && chart.exDates.length" class="txt-warn">
              检测到除权日 {{ chart.exDates.length }} 个（最近 {{ chart.exDates[chart.exDates.length - 1] }}）
            </span>
          </div>
          <!-- 画布常驻：勿用 v-if 拆掉，否则 echarts 实例绑在已销毁 DOM 上会空白 -->
          <div ref="chart" class="chart-box" />
        </div>
      </div>

      <!-- ============ 三重过滤实测 + 口径 ============ -->
      <div class="two-col">
        <div class="block">
          <div class="block-head">
            <h3>「优化版」三重过滤的实测效果</h3>
            <span class="hint">基于当前列表实时计算，不是抄报告</span>
          </div>
          <div class="block-body">
            <table class="mini-table">
              <thead>
                <tr>
                  <th>过滤</th>
                  <th class="num">通过</th>
                  <th class="num">覆盖率</th>
                  <th>说明</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>基线（无过滤）</td>
                  <td class="num">{{ rows.length }}</td>
                  <td class="num">100.0%</td>
                  <td class="txt-muted">九转信号全集</td>
                </tr>
                <tr v-for="f in filterStats" :key="f.key">
                  <td>{{ f.label }}</td>
                  <td class="num">{{ f.pass }}</td>
                  <td class="num">{{ f.cov }}</td>
                  <td :class="f.tone">{{ f.note }}</td>
                </tr>
                <tr>
                  <td>F1+F2+F3</td>
                  <td class="num">{{ comboPass }}</td>
                  <td class="num">{{ comboCoverage }}</td>
                  <td :class="comboPass <= 2 ? 'txt-down' : 'txt-muted'">
                    三个叠加后样本通常所剩无几 —— 慎用
                  </td>
                </tr>
              </tbody>
            </table>
            <p class="fnote">
              研究报告第五章逐个检验过这三个过滤器（样本为 2015 年以来的全部低九信号，持有 3 日毛均值）：
              <b>F2 放量是唯一显著正向</b>（0.696% vs 基线 0.104%，但砍掉约 89% 信号）；
              <b>F1 反而有害</b>（站上 MA20 −0.048% / ADX&lt;25 −0.297%）；
              <b>F1+F2+F3 叠加后毛均值转负（−0.385%）且样本只剩 1.1%</b>。
              因此<b>默认三个都不勾</b>，把实测值摆在旁边是为了避免「勾上就以为更准」。
            </p>
          </div>
        </div>

        <div class="block">
          <div class="block-head"><h3>口径、局限与实现要点</h3></div>
          <div class="block-body">
            <dl class="kv">
              <dt>信号定义</dt>
              <dd>低九：连续 9 根 <code>close &lt; close[−4]</code>；高九反之。任一条件中断则计数归零；<b>越过 9 后不重置</b>（继续 10/11/12…）</dd>
              <dt>比较基准</dt>
              <dd>用<b>该股自身 K 线序列</b>往前数 4 根（停牌日无 K 线，自然跳过）—— 与行情软件同口径</dd>
              <dt>复权口径</dt>
              <dd><b>不复权</b>（<code>daily_bar</code>）。东财盘面的九转也用不复权价 ⇒ 本页位置与它一致；代价是除权跳空可能造假信号，已用 <code>pre_close ≠ 前收</code> 检测并打标</dd>
              <dt>数据源选择</dt>
              <dd>刻意<b>不用</b> <code>market_daily_hfq</code>（后复权）：它是本地研究表、<b>线上为空</b>；<code>daily_bar</code> 是每日 CSV 的直连下游，两端都有且自动更新，<b>不需要新增同步链路</b></dd>
              <dt>可回溯范围</dt>
              <dd>下拉可选最近 {{ (meta.dates || []).length }} 个交易日（本库 <code>daily_bar</code> 只保留近 84 天，这是它的固有边界）</dd>
              <dt>未覆盖</dt>
              <dd>TD Sequential 的 Countdown 段（13 计数）未实现；本页不做收益展示与交易口径</dd>
              <dt>性能</dt>
              <dd>实时计算，按「信号日 + imported_at + 当日行数」缓存；首次约 1.3s，之后 &lt;50ms</dd>
            </dl>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="loading-wrap" v-loading="loading" element-loading-text="正在计算九转信号..." />
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { CandlestickChart, LineChart, ScatterChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { request } from '../utils/http.js'
import { stockstarCodeLink, eastmoneyCodeLink } from '../utils/stockLink.js'

echarts.use([CandlestickChart, LineChart, ScatterChart, GridComponent, TooltipComponent, CanvasRenderer])

const RED = '#F56C6C'
const GREEN = '#67C23A'
const BLUE = '#409EFF'
const ORANGE = '#E6A23C'
const MAX_SEQ = 12

export default {
  name: 'MagicNineTurn',
  props: {
    visible: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      error: '',
      meta: null,
      dates: [],
      date: '',
      dir: 'all',
      pool: 'all',
      exact: false,
      f1: false,
      f2: false,
      f3: false,
      rows: [],
      chart: null,
      chartCode: '',
      chartLoading: false,
      chartError: '',
      instance: null,
      resizeObserver: null,
    }
  },
  computed: {
    maxSeq() {
      return MAX_SEQ
    },
    market() {
      return (this.meta && this.meta.market) || {}
    },
    pools() {
      return (this.meta && this.meta.pools) || { all: 0, top500: 0, p75: 0 }
    },
    params() {
      return (this.meta && this.meta.params) || { volMult: 1.2, volLookback: 20 }
    },
    // 前端本地过滤：切换过滤器零延迟，且覆盖率可精确计算
    shownRows() {
      return this.rows.filter((r) => {
        if (this.f1 && !r.f1Pass) return false
        if (this.f2 && !r.f2Pass) return false
        if (this.f3 && !r.f3) return false
        return true
      })
    },
    filterActive() {
      return this.f1 || this.f2 || this.f3
    },
    lowCount() {
      return this.shownRows.filter((r) => r.dir === 'down').length
    },
    highCount() {
      return this.shownRows.filter((r) => r.dir === 'up').length
    },
    scopeText() {
      const poolName = { all: '全市场', top500: 'Top500', p75: 'P75 机构池' }[this.pool] || this.pool
      const dirName = { all: '全部方向', down: '低九', up: '高九' }[this.dir] || this.dir
      return `${poolName} · ${dirName} · ${this.exact ? '计数 = 9' : '计数 ≥ 9'}`
    },
    covText() {
      if (!this.rows.length) return ''
      const pct = (n) => `${((n / this.rows.length) * 100).toFixed(0)}%`
      return `覆盖率：F1 ${pct(this.rows.filter(r => r.f1Pass).length)} · F2 ${pct(this.rows.filter(r => r.f2Pass).length)} · F3 ${pct(this.rows.filter(r => r.f3).length)}`
    },
    f1Tip() {
      return '市场环境：合成等权指数站上 MA20，或 ADX(14) < 25（震荡市）。研究报告实测该过滤器反而降低收益（持有 3 日毛均值 −0.048% / −0.297%）'
    },
    f2Tip() {
      return `信号日成交量 ≥ 前 ${this.params.volLookback} 日均量的 ${this.params.volMult} 倍。研究报告实测：唯一显著正向的过滤器（毛均值 0.696% vs 基线 0.104%）`
    },
    f3Tip() {
      return '信号日收盘价站上 MA5。研究报告实测：影响很小（毛均值 0.164% vs 基线 0.104%）'
    },
    filterStats() {
      const n = this.rows.length
      const cov = (k) => (n ? `${((k / n) * 100).toFixed(1)}%` : '—')
      const c1 = this.rows.filter((r) => r.f1Pass).length
      const c2 = this.rows.filter((r) => r.f2Pass).length
      const c3 = this.rows.filter((r) => r.f3).length
      return [
        {
          key: 'f1',
          label: 'F1 非下跌趋势',
          pass: c1,
          cov: cov(c1),
          note: c1 === n ? '今日全通过（市场不在下跌趋势）' : c1 === 0 ? '今日全不通过（下跌趋势中）' : '市场级过滤器，通常全通过或全不通过',
          tone: 'txt-muted',
        },
        {
          key: 'f2',
          label: `F2 放量 ≥${this.params.volMult}×`,
          pass: c2,
          cov: cov(c2),
          note: '唯一显著正向的过滤器（代价：砍掉大部分信号）',
          tone: 'txt-up',
        },
        {
          key: 'f3',
          label: 'F3 站上 MA5',
          pass: c3,
          cov: cov(c3),
          note: '影响很小，可作诊断开关',
          tone: 'txt-muted',
        },
      ]
    },
    comboPass() {
      return this.rows.filter((r) => r.f1Pass && r.f2Pass && r.f3).length
    },
    comboCoverage() {
      return this.rows.length ? `${((this.comboPass / this.rows.length) * 100).toFixed(1)}%` : '—'
    },
    chartTitle() {
      if (!this.chart) return '九转计数图'
      return `${this.chart.code} ${this.chart.name} · ${this.chart.priceCaliber}日K · TD9 Setup 计数`
    },
  },
  watch: {
    visible(val) {
      if (val) this.$nextTick(() => this.ensureChartPaint())
    },
    loading(val) {
      // 首屏：信号列表请求结束、整页 DOM 才挂载，需再画一次
      if (!val) this.$nextTick(() => this.ensureChartPaint())
    },
  },
  mounted() {
    this.loadAll()
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.instance && this.instance.resize())
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.instance) {
      this.instance.dispose()
      this.instance = null
    }
  },
  methods: {
    stockstarCodeLink,
    eastmoneyCodeLink,
    signed(v) {
      if (v === null || v === undefined) return '—'
      return `${v >= 0 ? '+' : ''}${v}`
    },
    clsOf(v) {
      if (v === null || v === undefined) return ''
      return v >= 0 ? 'txt-up' : 'txt-down'
    },
    shortDate(d) {
      return String(d || '').slice(5)
    },
    dLabel(d) {
      const isLatest = this.meta && d === this.meta.latestDate
      return isLatest ? `${d}（最新）` : d
    },
    sparkHeight(r) {
      const max = Math.max(1, ...this.meta.recent.map((x) => x.down + x.up))
      return `${Math.max(8, ((r.down + r.up) / max) * 26)}px`
    },
    async loadAll() {
      this.loading = true
      this.error = ''
      try {
        const meta = await request('/api/td9/meta')
        this.meta = meta
        this.dates = meta.dates || []
        if (!this.date || !this.dates.includes(this.date)) this.date = meta.latestDate
        await this.loadSignals()
      } catch (err) {
        this.error = err.message || '加载九转数据失败'
      } finally {
        this.loading = false
      }
    },
    async loadSignals() {
      if (!this.date) return
      this.loading = true
      this.error = ''
      try {
        const qs = new URLSearchParams({
          date: this.date,
          dir: this.dir,
          pool: this.pool,
          ...(this.exact ? { exact: '1' } : {}),
        })
        const res = await request(`/api/td9/signals?${qs.toString()}`)
        this.rows = res.rows || []
        // 选中股票：保持原选（若还在列表里），否则取第一行
        const keep = this.rows.some((r) => r.code === this.chartCode)
        if (!keep) this.chartCode = this.rows.length ? this.rows[0].code : ''
        if (this.chartCode) await this.loadChart()
        else {
          this.chart = null
          if (this.instance) this.instance.clear()
        }
      } catch (err) {
        this.error = err.message || '加载信号列表失败'
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    async loadChart() {
      if (!this.chartCode) return
      this.chartLoading = true
      this.chartError = ''
      try {
        this.chart = await request(`/api/td9/chart?code=${this.chartCode}`)
      } catch (err) {
        this.chartError = err.message || '加载K线失败'
        this.chart = null
        if (this.instance) this.instance.clear()
      } finally {
        this.chartLoading = false
        // 等 loading 关掉、画布稳定后再渲染（避免首屏/切行时 DOM 尚未就绪）
        this.$nextTick(() => this.ensureChartPaint())
      }
    },
    onRowClick(row) {
      if (!row) return
      if (row.code === this.chartCode) {
        this.ensureChartPaint()
        return
      }
      this.chartCode = row.code
      this.loadChart()
    },
    rowClass({ row }) {
      return row.code === this.chartCode ? 'row-active' : ''
    },
    ensureChartPaint() {
      if (!this.visible || this.loading) return
      this.renderChart()
    },
    renderChart() {
      const el = this.$refs.chart
      if (!el || !this.chart || !(this.chart.bars || []).length) return

      // DOM 被重建后旧实例仍指向已销毁节点 → 画面空白；检测到则重建
      if (this.instance) {
        const dom = typeof this.instance.getDom === 'function' ? this.instance.getDom() : null
        if (!dom || dom !== el || !el.isConnected) {
          try { this.instance.dispose() } catch (_) { /* ignore */ }
          this.instance = null
        }
      }
      if (!this.instance) {
        this.instance = echarts.init(el)
        if (this.resizeObserver) this.resizeObserver.observe(el)
      }
      const c = this.chart
      const bars = c.bars || []
      const dates = bars.map((b) => b.date)
      const kdata = bars.map((b) => [b.open, b.close, b.low, b.high])
      const lo = Math.min(...bars.map((b) => b.low))
      const hi = Math.max(...bars.map((b) => b.high))
      const pad = (hi - lo) * 0.16 || 1

      const downPts = []
      const upPts = []
      const bubbles = []
      for (let i = 0; i < bars.length; i += 1) {
        const dn = c.down[i]
        const up = c.up[i]
        if (dn >= 1 && dn <= 9) downPts.push([i, bars[i].low - pad * 0.08, dn])
        if (up >= 1 && up <= 9) upPts.push([i, bars[i].high + pad * 0.08, up])
        if (dn === 9) bubbles.push({ value: [i, bars[i].low, '九'], itemStyle: { color: RED } })
        if (up === 9) bubbles.push({ value: [i, bars[i].high, '九'], itemStyle: { color: GREEN } })
      }

      this.instance.setOption({
        animation: false,
        grid: { left: 58, right: 24, top: 22, bottom: 34 },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          formatter: (ps) => {
            const i = ps[0].dataIndex
            const b = bars[i]
            let out = `${b.date}<br/>开 ${b.open} &nbsp;收 ${b.close}<br/>低 ${b.low} &nbsp;高 ${b.high}`
            if (c.down[i]) out += `<br/>低九方向计数：<b>${c.down[i]}</b>`
            if (c.up[i]) out += `<br/>高九方向计数：<b>${c.up[i]}</b>`
            if (b.volume) out += `<br/>成交量 ${b.volume.toLocaleString()}`
            return out
          },
        },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: '#dcdfe6' } },
          axisLabel: { color: '#909399', fontSize: 11, interval: 5, formatter: (v) => String(v).slice(5) },
          axisTick: { show: false },
        },
        yAxis: {
          scale: true,
          min: Number((lo - pad).toFixed(3)),
          max: Number((hi + pad).toFixed(3)),
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f2f6fc' } },
          axisLabel: { color: '#909399', fontSize: 11, formatter: (v) => v.toFixed(2) },
        },
        series: [
          {
            name: 'K',
            type: 'candlestick',
            data: kdata,
            itemStyle: { color: RED, color0: GREEN, borderColor: RED, borderColor0: GREEN },
          },
          {
            name: '低九计数',
            type: 'scatter',
            data: downPts,
            symbolSize: 1,
            itemStyle: { color: 'transparent' },
            label: { show: true, position: 'bottom', color: RED, fontSize: 10, formatter: (p) => p.value[2] },
            silent: true,
            tooltip: { show: false },
          },
          {
            name: '高九计数',
            type: 'scatter',
            data: upPts,
            symbolSize: 1,
            itemStyle: { color: 'transparent' },
            label: { show: true, position: 'top', color: GREEN, fontSize: 10, formatter: (p) => p.value[2] },
            silent: true,
            tooltip: { show: false },
          },
          {
            name: '九转日',
            type: 'scatter',
            data: bubbles,
            symbolSize: 15,
            label: { show: true, color: '#fff', fontSize: 10, formatter: '九' },
            z: 10,
          },
          { name: 'MA5', type: 'line', data: c.ma5, smooth: true, symbol: 'none', lineStyle: { width: 1.2, color: ORANGE }, connectNulls: true },
          { name: 'MA20', type: 'line', data: c.ma20, smooth: true, symbol: 'none', lineStyle: { width: 1.2, color: BLUE }, connectNulls: true },
        ],
      }, true)
      this.instance.resize()
    },
    exportCsv() {
      const head = ['代码', '名称', '方向', '计数', '信号日', '收盘', '涨跌幅%', '量比', '距MA5%', '流通市值(亿)', '行业', 'F1', 'F2', 'F3', '除权']
      const lines = this.shownRows.map((r) => [
        r.code,
        r.name,
        r.dir === 'down' ? '低九' : '高九',
        r.count,
        this.date,
        r.close,
        r.changePct,
        r.volRatio,
        r.ma5Dist,
        r.cap,
        r.industry,
        r.f1Pass ? 1 : 0,
        r.f2Pass ? 1 : 0,
        r.f3 ? 1 : 0,
        r.exDiv ? '除权' : '',
      ])
      const csv = [head, ...lines]
        .map((row) => row.map((v) => (v === null || v === undefined ? '' : String(v))).join(','))
        .join('\n')
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `td9-signals-${this.date}-${this.dir}-${this.pool}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  },
}
</script>

<style scoped>
.td9-page {
  padding: 4px;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 14px;
}

.toolbar-label {
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
}

.toolbar-fill {
  flex: 1 1 auto;
}

.toolbar-sep {
  width: 1px;
  height: 18px;
  background: #e4e7ed;
}

.sel-date {
  width: 168px;
}

.sel-pool {
  width: 168px;
}

.cov-hint {
  font-size: 12px;
  color: #909399;
}

.caliber-chip {
  font-size: 12px;
  color: #909399;
  border: 1px dashed #dcdfe6;
  border-radius: 10px;
  padding: 1px 8px;
}

.error-bar {
  margin-bottom: 14px;
}

.stat-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.stat-card {
  background: #f8f9fb;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 14px;
  min-width: 0;
}

.stat-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  color: #303133;
  display: flex;
  align-items: center;
}

.stat-value-sm {
  font-size: 14px;
  font-weight: 500;
}

.stat-sub {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
  line-height: 1.5;
}

.spark {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  height: 28px;
}

.spark > div {
  width: 16px;
  border-radius: 2px 2px 0 0;
}

.ml6 {
  margin-left: 6px;
}

.ml-auto {
  margin-left: auto;
}

.notice-trigger {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #f5c6cb;
  border-radius: 14px;
  background: #fef0f0;
  color: #f56c6c;
  font-size: 12px;
  cursor: help;
  outline: none;
}

.notice-trigger:hover {
  background: #fde2e2;
  border-color: #f56c6c;
}

.notice-trigger i {
  font-size: 14px;
}

.block {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  margin-bottom: 14px;
}

.block-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #ebeef5;
}

.block-head h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.hint {
  font-size: 12px;
  color: #909399;
}

.block-body {
  padding: 12px 14px;
}

.legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  padding: 8px 14px 10px;
  font-size: 12px;
  color: #606266;
}

.legend .dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
}

.dot-red {
  background: #F56C6C;
}

.dot-green {
  background: #67C23A;
}

.legend .line {
  display: inline-block;
  width: 15px;
  height: 2px;
  margin-right: 5px;
  vertical-align: middle;
}

.line-ma5 {
  background: #E6A23C;
}

.line-ma20 {
  background: #409EFF;
}

.chart-box {
  width: 100%;
  height: 380px;
}

.chart-panel {
  min-height: 380px;
  position: relative;
}

.chart-error {
  margin-bottom: 8px;
}

.chart-loading {
  height: 380px;
}

.two-col {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
  gap: 14px;
}

.mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.mini-table th,
.mini-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f2f6fc;
  text-align: left;
}

.mini-table th {
  color: #606266;
  font-weight: 500;
  background: #fafafa;
}

.mini-table .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.fnote {
  font-size: 12.5px;
  color: #606266;
  line-height: 1.8;
  margin: 10px 0 0;
}

.kv {
  display: grid;
  grid-template-columns: 92px 1fr;
  gap: 6px 12px;
  font-size: 12.5px;
  margin: 0;
}

.kv dt {
  color: #909399;
}

.kv dd {
  margin: 0;
  color: #303133;
  line-height: 1.7;
}

.count-run {
  color: #E6A23C;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.seq {
  display: inline-flex;
  gap: 2px;
}

.seq i {
  width: 7px;
  height: 14px;
  border-radius: 1px;
  background: #ebeef5;
}

.seq i.on-b {
  background: #F56C6C;
}

.seq i.on-s {
  background: #67C23A;
}

.txt-up {
  color: #F56C6C;
}

.txt-down {
  color: #67C23A;
}

.txt-warn {
  color: #E6A23C;
}

.txt-muted {
  color: #909399;
}

.txt-strong {
  font-weight: 700;
  color: #303133;
}

.loading-wrap {
  min-height: 320px;
}

.td9-page >>> .el-table .row-active td {
  background: #ecf5ff !important;
}

.td9-page >>> .el-table__body tr {
  cursor: pointer;
}

code {
  background: #f5f7fa;
  border: 1px solid #ebeef5;
  border-radius: 3px;
  padding: 0 4px;
  font-size: 12px;
}

@media (max-width: 768px) {
  .two-col {
    grid-template-columns: 1fr;
  }

  .stat-cards {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }

  .chart-box {
    height: 300px;
  }
}
</style>

<!-- popover 挂到 body，需非 scoped -->
<style>
.td9-notice-popper {
  padding: 12px 14px !important;
}

.td9-notice-pop .td9-notice-title {
  font-size: 13px;
  font-weight: 600;
  color: #f56c6c;
  margin-bottom: 8px;
}

.td9-notice-pop p {
  margin: 0 0 8px;
  font-size: 12.5px;
  line-height: 1.7;
  color: #606266;
}

.td9-notice-pop p:last-child {
  margin-bottom: 0;
}
</style>
